import { Component, Input, OnInit, ViewEncapsulation, ViewChild, ElementRef, AfterViewInit, OnDestroy, inject, Output, EventEmitter } from '@angular/core';
import { RuleEditorService } from '../../services/rule-editor.service';
import { IRuleSettings, IUpdateRuleRequest, IValidateRuleRequest } from '../../models/rule-editor.model';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { ICreateRuleInGroupRequest } from '../../models/rule-editor.model';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar} from '@angular/material/snack-bar';

import { Store } from '@ngrx/store';
import {  Subscription } from 'rxjs';
import { MeasuresActions } from '@store/actions/measures/measures.actions';
import * as fromMeasures from '@store/selectors/measures/measures.selectors'


//$rule and $ce are defined in src/assets/js/codeeffects/editor.js
declare var $rule: any;

@Component({
  selector: 'app-rule-editor',
  imports: [MatButtonModule, CommonModule, MatExpansionModule,MatIconModule],
  templateUrl: './rule-editor.component.html',
  styleUrl: './rule-editor.component.css',
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true 
})

export class RuleEditorComponent implements AfterViewInit, OnDestroy, OnInit {

	@Input() criteriaId: number = 0;
	@Input() componentId: number = 0;
	@Input() ruleGroupId: number = 0;
	@Input() ruleId: number = 0;
	@Input() editorID: string = "";
	@Input() isModal: boolean = false;

	@ViewChild('container', { static: true }) container!: ElementRef;
	@Output() ruleSavedEvent = new EventEmitter<boolean>();

	groupName = "";
	isLoadFromFile = false;
	ruleNumberCount = 0;
	private editorInstance: any;
	private readonly store = inject(Store);
	private editorService = inject(RuleEditorService);
	private subscription = new Subscription();
	private _snackBar = inject(MatSnackBar);

	headerText: string = "";
	
	ngOnInit():void {
		// get rule group id from newly created component
		this.subscription.add(
			this.store.select(fromMeasures.selectCriteriaList).subscribe((criteria) => {
				this.ruleGroupId = criteria.find( c => c.id == this.criteriaId)?.components.find( co => co.id == this.componentId)?.ruleGroupId || 0;
				this.ruleNumberCount =  criteria.find( c => c.id == this.criteriaId)?.components.find( co => co.id == this.componentId)?.rules?.length || 0;
				this.groupName = criteria.find( c => c.id == this.criteriaId)?.components.find( co => co.id == this.componentId)?.name || "";
			})
		)
	}

	ngAfterViewInit():void	{
		const newDiv = document.createElement('div');
		newDiv.id = this.editorID;

		this.container.nativeElement.appendChild(newDiv);

		this.loadRuleEditorSettings(this.editorID, this.ruleGroupId,this.isModal).then((data) =>{

			//There's an issue where navingating back places $rule into indetermined state. Clearing the context fixes that.
			this.editorInstance = $rule.Context.getControl(this.editorID);
			
			if (this.editorInstance != null)
				this.editorInstance.dispose();
			
			$rule.Context.clear();

			// Init the editor and store its reference in a global variable
			this.editorInstance = $rule.init(data.editorData);
			this.editorInstance.clear();

			// Load the source settings
			this.editorInstance.loadSettings(data.sourceData);

			if (!this.isModal)
				this.loadRule();      

		}).catch((err) => console.error(err));
	}
	
	async loadRuleEditorSettings(editorId: string, ruleGroupId: number, newRule: boolean): Promise<IRuleSettings> {
		return await this.editorService.getRuleEditorSettings(editorId, ruleGroupId,newRule);
	};

	// Client action method that loads rule from the storage.
	loadRule() {
		// Load the rule into the Rule Area by passing json to the editor
		try{
			this.editorService.getRuleData(this.ruleId).then(ruleData =>{
				this.ruleLoaded(ruleData);
			}).catch(err => {
				this.openSnackBar(`Error Loading Rule: ${this.ruleId}`)
			});
		}catch(err){

		}
	};

	ruleFileSelected(event: any) {		
		const file: File = event.target.files[0];

		if (file) {
			const reader = new FileReader();
			reader.onload = (e: any) => {
				const fileContent = e.target.result;
				try {
					this.editorService.getRuleFromXML(this.editorID, fileContent).then( ruleData =>{ 
						this.ruleLoaded(ruleData); 
						this.evaluateRule();
					});
				} catch (error) {
					console.error("Error parsing the file content", error);
				}
			};
			reader.readAsText(file);
			this.isLoadFromFile = false;
		}
	}

	// Handler of the successful loadRule() call
	ruleLoaded(ruleData: any) {
		// Load the rule into the Rule Area by passing json to the editor
		this.editorInstance.loadRule(ruleData);
  	};

    // Client action method that deletes the current rule from the storage.
	deleteRule() {
		this.editorService.deleteRule(this.ruleId).then(() =>{
			this.ruleDeleted();
		}).catch(err =>{
			console.log(err);
		}); 	
	};
	
	// Handler of the successful deleteRule() call
	ruleDeleted() {
		// Let the editor know that there were no errors and
		// the rule of codeeffects.getRuleId() ID was deleted successfully
		// this.editorInstance.deleted(this.editorInstance.getRuleId());

		// Clear the Rule Area (we just deleted the rule,
		// there is no point of displaying it anymore)
		this.editorInstance.clear();

		// Notify user that the rule was deleted
		console.log("Rule was deleted successfully");
	};

	// Client action method that saves the new or existing rule in the storage.

	saveNewRuleInGroup(){
		const request: ICreateRuleInGroupRequest = {
			editorID: this.editorID,
			name: `${this.groupName}_RuleGroup_${this.ruleGroupId}`,
			definition: JSON.stringify(this.editorInstance.extract()),
			createUserID: 0,
			ruleGroupId: this.ruleGroupId
		};		
		
		this.editorService.createRuleInGroup(request).then((data) =>{
			this.ruleSaved(data);		
		});
	}

	updateRule(){
		const request: IUpdateRuleRequest = {
			editorID: this.editorID,
			name: `${this.groupName}_RuleGroup_${this.ruleGroupId}`,
			definition: JSON.stringify(this.editorInstance.extract()),
			ruleID: this.ruleId,
			modifyUserID: 0
		};		
		
		this.editorService.updateRule(request).then((data) =>{
			this.ruleUpdated(data);		
		});
	}

	// Handler of the successful saveRule() call
	ruleSaved(data: any) {
		console.log(data);
        if (!data.id) {
			// The rule is invalid. Pass the received invalid client data to the editor
			// Notify user
			this.openSnackBar("The rule is not valid");
		}
		else {

			this.store.dispatch(MeasuresActions.addNewRule({ criteriaId: this.criteriaId, componentId: this.componentId,
				ruleId: data.id, sortOrder: (this.ruleNumberCount + 1) }));

			// Inform user
			this.ruleSavedEvent.emit();
			this.openSnackBar(`Rule ${data.id} Saved Successfully.`)
		}
	};

	// Handler of the successful updatedRule() call
	ruleUpdated(data: any) {

		if (data.isRuleEmpty) console.log("The rule is empty"); // The Rule Area is empty
		else if (!data.isRuleValid) {
			// The rule is invalid. Pass the received invalid client data to the editor
			this.editorInstance.loadInvalids(data.clientInvalidData);

			// Notify user
			this.openSnackBar("The rule is not valid");
		}
		else {
			// Server returns rule ID using the Output property of ProcessingResult C# type.
			// The editor needs this ID if the saved rule was a new rule. In any case, pass it to the editor
			this.editorInstance.saved(data.output);

			// Inform user
			this.openSnackBar("The rule was saved successfully");
		}
	};

	evaluateRule(){
		const request: IValidateRuleRequest = {
			editorId: this.editorID,
			definition:  JSON.stringify(this.editorInstance.extract()),
		}

		this.editorService.validateRule(request).then( 
			(result) => this.ruleEvaluated(result))
				.catch( err => {
					this.openSnackBar('The Rule Is Not Valid')
				});
	}

	ruleEvaluated(data: any){
		this.openSnackBar('The Rule Is Valid')
	}

	cancel(){
		// reset old editor data
		this.editorInstance.clear();
		this.loadRule(); 
	}

	clear(){
		this.editorInstance.clear();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();

		if (this.editorInstance != null) 
			this.editorInstance.dispose(); 		
	}

	openSnackBar(message: string) {
		this._snackBar.open(message, '',{
		  duration:  4000,
		  horizontalPosition: 'center',
		  verticalPosition : 'top',
		});
	  }
	
}





